import { ApiError, type ImportCheck, type ImportIssue, type ImportRun, type ImportSource, type ImportState, type SourceAccess, type SourceMode } from "./api";
import { age } from "./format";
import { memberName } from "./memberNames";
import type { SortColumn } from "./sort";

export type Owner = "Dispatch" | "Sales" | "Office" | "Administrator";

/** Rule T-5, SO-408: the screen can change a field of the sheet, and this warning shows beside it. */
export const SHEET_WARNING = "The sheet import writes over this field when the sheet changes, and each night.";

export const sheetHeld = (fromSheet: readonly string[] | undefined, field: string) => Boolean(fromSheet?.includes(field));

/** Design 26.6: what each issue code means, and who owns the clean-up (SO-418). */
export const CODES: Record<string, { text: string; owner: Owner }> = {
  no_point: { text: "No point for the front door", owner: "Dispatch" },
  pin_to_check: { text: "Rough geocode: check the pin on the map", owner: "Dispatch" },
  bad_point: { text: "Latitude or longitude is not a point", owner: "Dispatch" },
  point_gone: { text: "No Latitude and Longitude: the last good point stays", owner: "Dispatch" },
  bad_latitude: { text: "Latitude is not a number", owner: "Dispatch" },
  bad_longitude: { text: "Longitude is not a number", owner: "Dispatch" },
  number_format: { text: "Account number does not match the scheme", owner: "Office" },
  duplicate_number: { text: "Account number is in the sheet two times", owner: "Office" },
  missing_name: { text: "No customer name: the row is not imported", owner: "Office" },
  missing_status: { text: "No known status: the row is not imported", owner: "Dispatch" },
  unknown_status: { text: "Status is not NEW, REG, CALL, HOLD or OFF", owner: "Dispatch" },
  missing_area: { text: "No area: the service plan does not change", owner: "Dispatch" },
  bad_area: { text: "Area is not like S3.04", owner: "Dispatch" },
  bad_week_group: { text: "WkGroup is not like 1-3", owner: "Dispatch" },
  missing_rate: { text: "No gal/wk: the app rate stays, or 0 for a new account", owner: "Dispatch" },
  missing_week_group: { text: "No WkGroup: the app visit weeks stay", owner: "Dispatch" },
  missing_window: { text: "No Start and End: the app window stays", owner: "Dispatch" },
  missing_duration: { text: "No Duration: the app minutes stay", owner: "Dispatch" },
  bad_number: { text: "Text in a number column", owner: "Dispatch" },
  bad_time: { text: "Start or End is not a time", owner: "Dispatch" },
  half_window: { text: "Only one of Start and End: the app window stays", owner: "Dispatch" },
  window_order: { text: "End is not after Start: the app window stays", owner: "Dispatch" },
  long_service: { text: "Duration is more than 240 minutes: the app minutes stay", owner: "Dispatch" },
  many_containers: { text: "Tank Gal is not the total of the containers", owner: "Dispatch" },
  bad_pickup: { text: "Text in a Raw or Scrap cell", owner: "Dispatch" },
  scrap_over_raw: { text: "Scrap is more than Raw: the pickup is not imported", owner: "Dispatch" },
  pickup_changed: { text: "A pickup changed after the import", owner: "Office" },
  pickup_changed_kept: { text: "A pickup changed in the sheet, but the app pickup stays", owner: "Office" },
  sheet_changed_after_cutover: { text: "The sheet changed a field that the app owns: the app value stays", owner: "Office" },
  bad_week_code: { text: "Week code is not a real week", owner: "Office" },
  deleted_on_screen: { text: "Account deleted on the screen", owner: "Administrator" },
  too_long: { text: "Text is too long", owner: "Office" },
  sales_only: { text: "Only in the sales sheet", owner: "Sales" },
  salesperson_not_member: { text: "Salesperson is not a user", owner: "Sales" },
  status_mismatch: { text: "The account status and the service status do not agree", owner: "Sales" },
  unknown_account_status: { text: "Account status is not Active, Hold or Inactive", owner: "Sales" },
  missing_tab: { text: "A tab is missing: nothing imported", owner: "Administrator" },
  missing_column: { text: "A required header is missing: nothing imported", owner: "Administrator" },
  no_header: { text: "No header row: nothing imported", owner: "Administrator" },
};

export const codeText = (code: string) => CODES[code]?.text ?? code.replace(/_/g, " ");
export const codeOwner = (code: string): Owner => CODES[code]?.owner ?? "Administrator";

export const STATE_TEXT: Record<ImportState, string> = {
  ok: "OK",
  refused: "Stopped: nothing written",
  failed: "Failed: nothing written",
};

export const CHECK_TEXT: Record<ImportCheck["state"], string> = { ...STATE_TEXT, unchanged: "No change in the sheet" };

/** SO-4016: an hourly import that has not run for this long shows a warning. */
export const CHECK_LATE_MS = 2 * 60 * 60_000;

/** An open Import page loads the runs again this often, so the age of the check stays true. */
export const CHECK_RELOAD_MS = 15 * 60_000;

/** A failed reload keeps the runs on the page, unless the sign-in has ended. */
export function afterReload<T, F extends string>(old: T[] | F | undefined, failure: F): T[] | F {
  return Array.isArray(old) && failure !== "session" ? old : failure;
}

export interface CheckLine {
  text: string;
  state: string;
  warn: boolean;
}

/** The line above the runs: when the import last read the sheet, and what it found. */
export function checkLine(check: ImportCheck | null | undefined, now: Date, time: (at: string) => string): CheckLine | null {
  if (!check) return null;
  const late = now.getTime() - new Date(check.at).getTime() > CHECK_LATE_MS;
  return {
    text: late
      ? `The hourly import has not run since ${time(check.at)} (${age(check.at, now)}). Tell an operator. Last result:`
      : `Last check ${age(check.at, now)}, ${time(check.at)}:`,
    state: CHECK_TEXT[check.state] ?? check.state,
    warn: late || check.state === "failed" || check.state === "refused",
  };
}

export const MODE_TEXT: Record<ImportRun["mode"], string> = { "dry-run": "Dry run", apply: "Apply" };

/** Design 26.11 F8: the mode of a source. Report is a dry run each hour; it writes no data. */
export const SOURCE_MODE_TEXT: Record<SourceMode, string> = {
  off: "Off",
  report: "Report: a dry run each hour",
  apply: "Apply: write the changes each hour",
};

export const KIND_TEXT: Record<ImportSource["kind"], string> = { service: "Service", sales: "Sales" };

/** The settings as one line, such as "sheet 1AbC…". An operator sets them; they hold no secret. */
export function settingsText(settings: ImportSource["settings"]): string {
  const keys = Object.keys(settings).sort();
  if (!keys.length) return "None";
  return keys.map((key) => `${key.replace(/_/g, " ")} ${String(settings[key])}`).join(", ");
}

const ACCESS_CODES: Record<string, string> = {
  ok: "OK",
  no_access: "No access: share the sheet with the reader account as Viewer",
  missing_tab: "A tab is missing",
  missing_column: "A required header is missing",
  no_header: "No header row",
  missing_file: "The fixture file is missing",
};

/** One line for the Test access result of a source: the code and its counts, never a value. */
export function accessLine(found: SourceAccess): string {
  const text = ACCESS_CODES[found.code] ?? found.code.replace(/_/g, " ");
  const detail = found.detail ?? {};
  const counts = found.ok && detail.columns !== undefined ? ` (header row ${detail.header_row}, ${detail.columns} columns, ${detail.weeks ?? 0} weeks)` : "";
  const status = detail.status ? ` (status ${detail.status})` : "";
  return `${KIND_TEXT[found.kind] ?? found.kind}: ${text}${counts}${status}`;
}

/** The service source decides the run: Run now needs apply, Dry run needs a mode that is not off. */
export function runChoices(sources: ImportSource[]): { dryRun: boolean; apply: boolean; paused: boolean } {
  const service = sources.find((source) => source.kind === "service");
  return {
    dryRun: Boolean(service && service.mode !== "off"),
    apply: service?.mode === "apply",
    paused: sources.some((source) => source.paused_at !== null),
  };
}

/** Why Run now or Dry run is not possible, or null when both are. */
export function runHint(choices: { dryRun: boolean; apply: boolean }, canRun: boolean): string | null {
  if (!canRun) return null;
  if (!choices.dryRun) return "Dry run needs the Service source in Report or Apply mode. Run now needs it in Apply mode.";
  return choices.apply ? null : "Run now needs the Service source in Apply mode.";
}

export const pauseText = (paused: boolean, canApply: boolean) =>
  paused ? `The hourly and the nightly import are paused.${canApply ? " Run now still works." : ""}` : "The hourly and the nightly import run again.";

/** Who paused the import. An older API sends only the member id, which the page does not show. */
export function pausedText(source: Pick<ImportSource, "paused_at" | "paused_by" | "paused_by_name">, when: (iso: string) => string): string {
  if (!source.paused_at) return "Each hour";
  const by = source.paused_by_name ?? (source.paused_by?.includes("@") ? source.paused_by : null);
  return `Paused ${when(source.paused_at)}${by ? ` by ${by.includes("@") ? memberName(by) : by}` : ""}`;
}

export type ImportAction = "mode" | "run" | "dry-run" | "test" | "pause";

const CONFLICT_TEXT: Record<ImportAction, string> = {
  mode: "The settings of this source do not allow this mode. Ask an operator to check the source.",
  run: "Run now needs the Service source in Apply mode. Reload the page to see the mode now.",
  "dry-run": "A dry run needs the Service source in Report or Apply mode. Reload the page to see the mode now.",
  test: "The source changed. Reload the page, then try again.",
  pause: "The source changed. Reload the page, then try again.",
};

/** Plain words for a failed import action, never the HTTP or the browser text. */
export function importFailure(e: unknown, action: ImportAction, online = typeof navigator === "undefined" || navigator.onLine): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (!status) return online ? "The import service did not answer. Check your connection, then try again." : "You are offline. Connect, then try again.";
  if (status === 401) return "Your session ended. Sign in again, then try again.";
  if (status === 403) return "Your role cannot change the import.";
  if (status === 404) return "This organization has no import source. An operator adds it.";
  if (status === 409) return CONFLICT_TEXT[action];
  if (status >= 500) return "The import service did not answer. Try again in a few minutes.";
  return "The import did not do this. Reload the page, then try again.";
}

export interface IssueGroup {
  code: string;
  text: string;
  owner: Owner;
  issues: ImportIssue[];
}

/** The issues of a run by code, the largest group first. */
export function groupIssues(issues: readonly ImportIssue[]): IssueGroup[] {
  const groups = new Map<string, ImportIssue[]>();
  for (const issue of issues) groups.set(issue.code, [...(groups.get(issue.code) ?? []), issue]);
  return [...groups.entries()]
    .map(([code, found]) => ({ code, text: codeText(code), owner: codeOwner(code), issues: found }))
    .sort((a, b) => b.issues.length - a.issues.length || a.code.localeCompare(b.code));
}

const count = (counts: Record<string, number>, key: string) => counts[key] ?? 0;

/** One line of what a run wrote, or would write in a dry run. */
export function changeText(run: Pick<ImportRun, "counts" | "mode" | "state">): string {
  if (run.state !== "ok") return "Nothing written";
  const c = run.counts;
  const parts = [
    [count(c, "accounts_new"), "new accounts"],
    [count(c, "accounts_changed"), "changed"],
    [count(c, "accounts_off"), "set to OFF"],
    [count(c, "pickups_new"), "pickups"],
    [count(c, "pickups_updated"), "pickups changed"],
    [count(c, "pickups_cleared"), "pickups removed"],
    [count(c, "pickups_kept"), "pickups changed in the sheet but kept in the app"],
    [count(c, "geocoded"), "geocoded"],
  ]
    .filter(([n]) => Number(n) > 0)
    .map(([n, label]) => `${n} ${label}`);
  const text = parts.length ? parts.join(", ") : "No change";
  return run.mode === "dry-run" ? `Would write: ${text}` : text;
}

export const RUN_COLUMNS: SortColumn<ImportRun>[] = [
  { key: "started", label: "Started", value: (r) => r.started_at, first: "desc" },
  { key: "mode", label: "Mode", value: (r) => r.mode, order: ["apply", "dry-run"] },
  { key: "state", label: "Result", value: (r) => r.state, order: ["refused", "failed", "ok"] },
  { key: "rows", label: "Rows", value: (r) => r.counts.rows ?? null, first: "desc", num: true },
  { key: "new", label: "New", value: (r) => r.counts.accounts_new ?? null, first: "desc", num: true },
  { key: "changed", label: "Changed", value: (r) => r.counts.accounts_changed ?? null, first: "desc", num: true },
  { key: "off", label: "Off", value: (r) => r.counts.accounts_off ?? null, first: "desc", num: true },
  { key: "pickups", label: "Pickups", value: (r) => r.counts.pickups_new ?? null, first: "desc", num: true },
  { key: "issues", label: "Issues", value: (r) => r.issues, first: "desc", num: true },
  { key: "actor", label: "By", value: (r) => r.actor.toLowerCase(), off: true },
];

export const ISSUE_ROW_LIMIT = 200;
